import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import EnchargeModal from './EnchargeModal';
import * as FiIcons from 'react-icons/fi';

const { FiHeart, FiEdit, FiShield, FiUsers, FiDownload, FiArrowRight, FiArrowLeft, FiClipboard, FiBookOpen } = FiIcons;

function PersonaSection() {
  const { type } = useParams();
  const [modalOpen, setModalOpen] = useState(false);

  const personaData = {
    christian: {
      title: 'Christian',
      subtitle: 'Mindset & Motivation',
      icon: FiHeart,
      color: 'rose',
      overview: "Your identity as a Christian author is your foundation. Before you write a single word, you need to understand that your calling to write comes from God. This isn't about platform or profit—it's about stewardship of the gifts and story He's given you.",
      actionStep: {
        title: "Reflect on Your Calling",
        description: "Spend 10 minutes in prayer and journaling. Ask God: 'What story or message have You placed on my heart?' Write down what comes to mind, no matter how small or incomplete it feels."
      },
      downloadable: {
        title: "Gospel-Centered Author Identity Worksheet",
        description: "A guided worksheet to help you discover and confirm your calling as a Christian author.",
        formId: "your-christian-form-id-here" // Replace with actual form ID
      },
      encouragement: "Remember: You don't need to have it all figured out. God uses broken vessels and incomplete stories for His glory.",
      nextSteps: [
        {
          title: "Ready to plan your book?",
          description: "Once you've confirmed your calling, it's time to create a concrete plan.",
          link: "/checklist",
          buttonText: "Start Book Planning",
          icon: FiClipboard
        }
      ]
    },
    creator: {
      title: 'Creator',
      subtitle: 'Book Planning & Writing',
      icon: FiEdit,
      color: 'amber',
      overview: "As a Creator, you transform ideas into words on the page. This is where vision becomes reality through planning, discipline, and faithful daily work. Your book needs structure, and your writing needs rhythm.",
      actionStep: {
        title: "Define Your Book's Core",
        description: "Complete this sentence: 'This book will help [specific reader] to [specific outcome] by [your unique approach].' Write it down and keep it visible while you write."
      },
      downloadable: {
        title: "StoryWise Planning Template",
        description: "A comprehensive template to help you plan and structure your book project.",
        formId: "your-creator-form-id-here" // Replace with actual form ID
      },
      encouragement: "Small steps lead to finished books. Focus on progress, not perfection.",
      nextSteps: [
        {
          title: "Create Your Book Plan",
          description: "Turn your ideas into a concrete, actionable plan with our interactive checklist.",
          link: "/checklist",
          buttonText: "Start Planning Now",
          icon: FiClipboard,
          featured: true
        }
      ]
    },
    caretaker: {
      title: 'Caretaker',
      subtitle: 'Publishing Roadmap',
      icon: FiShield,
      color: 'emerald',
      overview: "As a Caretaker, you shepherd your book from manuscript to published work. This involves understanding your options, making wise decisions about editing, design, and distribution, and protecting the integrity of your message.",
      actionStep: {
        title: "Research Your Publishing Path",
        description: "Decide between traditional and self-publishing. Spend 30 minutes researching three options in your chosen path. Write down the pros and cons of each."
      },
      downloadable: {
        title: "Publishing Decision Framework",
        description: "A step-by-step guide to help you choose the right publishing path for your book.",
        formId: "your-caretaker-form-id-here" // Replace with actual form ID
      },
      encouragement: "You don't need to navigate this alone. Seek wisdom, ask questions, and trust the process.",
      nextSteps: [
        {
          title: "Need to plan first?",
          description: "If you're still working on your manuscript, our planning checklist can help.",
          link: "/checklist",
          buttonText: "Book Planning Checklist",
          icon: FiBookOpen
        }
      ]
    },
    connector: {
      title: 'Connector',
      subtitle: 'Marketing Mindset & First Steps',
      icon: FiUsers,
      color: 'blue',
      overview: "As a Connector, you bridge the gap between your book and the readers who need it. Marketing isn't about manipulation—it's about service. You're not selling; you're connecting your message with hearts that need to hear it.",
      actionStep: {
        title: "Identify Your One Reader",
        description: "Think of one specific person who would benefit from your book. Write their name down. What's their biggest struggle? How will your book help them? Keep this person in mind as you write and share."
      },
      downloadable: {
        title: "GospelWise Marketing Starter Guide",
        description: "Learn gospel-centered marketing strategies that focus on serving your readers, not manipulating them.",
        formId: "f9a15b58-35d3-464d-b847-51d70ae3f6df" // Your actual Connector form ID
      },
      encouragement: "Marketing with gospel wisdom means leading with love, not pressure. Your readers are waiting for what you have to offer.",
      nextSteps: [
        {
          title: "Learn Gospel-Centered Marketing",
          description: "Discover how to share your work with a heart to serve.",
          link: "/marketing",
          buttonText: "Explore Marketing",
          icon: FiUsers
        }
      ]
    }
  };

  const persona = personaData[type];
  if (!persona) return <div>Persona not found</div>;

  const colorClasses = {
    rose: {
      bg: 'from-rose-50 to-pink-50',
      accent: 'bg-rose-100',
      text: 'text-rose-700',
      button: 'bg-rose-600 hover:bg-rose-700',
      border: 'border-rose-200'
    },
    amber: {
      bg: 'from-amber-50 to-yellow-50',
      accent: 'bg-amber-100',
      text: 'text-amber-700',
      button: 'bg-amber-600 hover:bg-amber-700',
      border: 'border-amber-200'
    },
    emerald: {
      bg: 'from-emerald-50 to-green-50',
      accent: 'bg-emerald-100',
      text: 'text-emerald-700',
      button: 'bg-emerald-600 hover:bg-emerald-700',
      border: 'border-emerald-200'
    },
    blue: {
      bg: 'from-blue-50 to-indigo-50',
      accent: 'bg-blue-100',
      text: 'text-blue-700',
      button: 'bg-blue-600 hover:bg-blue-700',
      border: 'border-blue-200'
    }
  };

  const colors = colorClasses[persona.color];

  const handleDownloadClick = () => {
    setModalOpen(true);
  };

  return (
    <div className={`min-h-screen bg-gradient-to-br ${colors.bg}`}>
      <div className="max-w-3xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-8">
          <div className={`inline-flex p-4 ${colors.accent} rounded-2xl mb-4`}>
            <SafeIcon icon={persona.icon} className={`w-12 h-12 ${colors.text}`} />
          </div>
          <h1 className="font-serif text-4xl text-gray-800 mb-2">
            The {persona.title}
          </h1>
          <p className={`text-xl font-medium ${colors.text}`}>
            {persona.subtitle}
          </p>
        </div>

        {/* Overview */}
        <div className={`bg-white/80 backdrop-blur-sm rounded-2xl p-8 mb-8 border ${colors.border}`}>
          <h2 className="font-serif text-2xl text-gray-800 mb-4">
            What This Means for You
          </h2>
          <p className="text-gray-700 leading-relaxed text-lg">
            {persona.overview}
          </p>
        </div>

        {/* Action Step */}
        <div className={`bg-white/80 backdrop-blur-sm rounded-2xl p-8 mb-8 border ${colors.border}`}>
          <h2 className="font-serif text-2xl text-gray-800 mb-4">
            Your Next Action Step
          </h2>
          <div className={`${colors.accent} rounded-xl p-6`}>
            <h3 className={`font-semibold text-lg mb-3 ${colors.text}`}>
              {persona.actionStep.title}
            </h3>
            <p className="text-gray-700 leading-relaxed">
              {persona.actionStep.description}
            </p>
          </div>
        </div>

        {/* Next Steps Integration */}
        {persona.nextSteps && persona.nextSteps.length > 0 && (
          <div className={`bg-white/80 backdrop-blur-sm rounded-2xl p-8 mb-8 border ${colors.border}`}>
            <h2 className="font-serif text-2xl text-gray-800 mb-6">
              Ready for the Next Step?
            </h2>
            <div className="space-y-4">
              {persona.nextSteps.map((step, index) => (
                <div key={index} className={`${step.featured ? colors.accent : 'bg-gray-50'} rounded-xl p-6 ${step.featured ? 'border-2 ' + colors.border : ''}`}>
                  <div className="flex items-start space-x-4">
                    <div className={`p-2 bg-white rounded-lg shadow-sm ${step.featured ? 'ring-2 ring-' + persona.color + '-200' : ''}`}>
                      <SafeIcon icon={step.icon} className={`w-5 h-5 ${colors.text}`} />
                    </div>
                    <div className="flex-1">
                      <h3 className={`font-semibold text-lg mb-2 ${step.featured ? colors.text : 'text-gray-800'}`}>
                        {step.title}
                      </h3>
                      <p className="text-gray-700 mb-4">
                        {step.description}
                      </p>
                      <Link 
                        to={step.link}
                        className={`inline-flex items-center space-x-2 ${step.featured ? colors.button : 'bg-gray-600 hover:bg-gray-700'} text-white px-4 py-2 rounded-lg transition-colors`}
                      >
                        <span>{step.buttonText}</span>
                        <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Downloadable with Modal */}
        

        {/* Encouragement */}
        <div className="text-center mb-8">
          <div className={`${colors.accent} rounded-xl p-6 border ${colors.border}`}>
            <p className="text-gray-700 italic text-lg leading-relaxed">
              "{persona.encouragement}"
            </p>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Link 
            to="/roadmap"
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            <SafeIcon icon={FiArrowLeft} className="w-4 h-4" />
            <span>Back to Roadmap</span>
          </Link>

          <Link 
            to="/next-steps"
            className={`${colors.button} text-white px-6 py-3 rounded-xl transition-colors flex items-center space-x-2`}
          >
            <span>Continue Journey</span>
            <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
          </Link>
        </div>
      </div>

      {/* Encharge Modal */}
      <EnchargeModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        formId={persona.downloadable.formId}
        title={persona.downloadable.title}
        description={persona.downloadable.description}
      />
    </div>
  );
}

export default PersonaSection;